import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * Public page that handles encoded receipt PDF download URLs.
 * URL format: /r/:encoded  (base64url-encoded filename)
 *
 * On mount, decodes the filename, constructs the Linode Object Storage URL,
 * fetches the PDF, and triggers an auto-download.
 */
const ReceiptDownload = () => {
    const { encoded } = useParams();
    const [status, setStatus] = useState("downloading"); // downloading | success | error
    const [downloadUrl, setDownloadUrl] = useState("");
    const [publicUrl, setPublicUrl] = useState("");
    const [meta, setMeta] = useState(null);
    const [isInApp, setIsInApp] = useState(false);

    const triggerDownload = (url, name) => {
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    useEffect(() => {
        // Detect In-App Browsers (WhatsApp, FB, etc)
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        if (ua.indexOf("WhatsApp") > -1 || ua.indexOf("FBAN") > -1 || ua.indexOf("FBAV") > -1) {
            setIsInApp(true);
        }

        const downloadReceipt = async () => {
            try {
                const filename = atob(encoded.replace(/-/g, "+").replace(/_/g, "/"));

                if (!filename || !filename.endsWith(".pdf")) {
                    setStatus("error");
                    return;
                }

                // Construct Public URL
                const fileUrl = `https://health-hub.in-maa-1.linodeobjects.com/receipt-maker/${filename}`;
                setPublicUrl(fileUrl);

                // Fetch Public Metadata (Securely using full encoded string as token)
                const apiBase = process.env.REACT_APP_BASE_URL.endsWith("/") 
                    ? process.env.REACT_APP_BASE_URL 
                    : `${process.env.REACT_APP_BASE_URL}/`;

                fetch(`${apiBase}api/receipt/meta/${encoded}`)
                    .then(res => res.json())
                    .then(data => {
                        if (data.success) setMeta(data);
                    })
                    .catch(e => console.error("Error fetching meta:", e));

                // Fetch the PDF for Blob (for desktop auto-trigger)
                const res = await fetch(fileUrl);
                if (res.ok) {
                    const blob = await res.blob();
                    const bUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
                    setDownloadUrl(bUrl);
                    if (!isInApp) triggerDownload(bUrl, filename);
                }

                setStatus("success");
            } catch (err) {
                console.error("Receipt download error:", err);
                setStatus("error");
            }
        };

        if (encoded) {
            downloadReceipt();
        }
    }, [encoded, isInApp]);

    return (
        <div
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "'Inter', 'Segoe UI', sans-serif",
                background: "#faf9f7",
                color: "#333",
                padding: "20px",
                textAlign: "center",
            }}
        >
            {status === "downloading" && (
                <>
                    <div className="spinner" />
                    <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, color: "#1a1a1a" }}>
                        Fetching your receipt...
                    </h2>
                    <p style={{ fontSize: 15, color: "#666", maxWidth: 300 }}>
                        Your document is being prepared for download.
                    </p>
                </>
            )}

            {status === "success" && (
                <div style={{
                    background: "#fff",
                    padding: "40px 30px",
                    borderRadius: "24px",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.04)",
                    maxWidth: "400px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center"
                }}>
                    <div style={{ 
                        fontSize: 50, 
                        marginBottom: 24, 
                        background: "#f0fdf4", 
                        width: 80, 
                        height: 80, 
                        borderRadius: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>🧾</div>

                    {meta ? (
                        <>
                            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: "#1a1a1a" }}>
                                Hello, {meta.patientName}
                            </h2>
                            <p style={{ fontSize: 15, color: "#666", marginBottom: 24 }}>
                                Your receipt with ID <strong style={{color: '#a51717'}}>{meta.rid}</strong> from <strong>Dr. {meta.doctorName}</strong> is ready.
                            </p>
                        </>
                    ) : (
                        <>
                            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: "#1a1a1a" }}>
                                Download Ready
                            </h2>
                            <p style={{ fontSize: 15, color: "#666", marginBottom: 24 }}>
                                Click the button below to save your receipt.
                            </p>
                        </>
                    )}

                    <a
                        href={publicUrl || downloadUrl}
                        download="Receipt.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "16px 32px",
                            background: "#a51717",
                            color: "#fff",
                            borderRadius: "14px",
                            textDecoration: "none",
                            fontWeight: "600",
                            fontSize: "17px",
                            boxShadow: "0 8px 25px rgba(165, 23, 23, 0.25)",
                            transition: "all 0.2s ease",
                            width: "100%",
                            justifyContent: "center"
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = "#8e1010";
                            e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = "#a51717";
                            e.currentTarget.style.transform = "translateY(0)";
                        }}
                    >
                        Save Receipt
                    </a>

                    {isInApp && (
                        <div style={{ 
                            marginTop: 24, 
                            padding: "12px", 
                            background: "#f8f9fa", 
                            borderRadius: "12px", 
                            fontSize: "13px", 
                            color: "#666",
                            border: "1px dashed #ddd"
                        }}>
                             💡 <strong>Tip:</strong> If the file doesn't save, tap the three dots <strong>(⋮)</strong> and select <strong>"Open in Browser"</strong>.
                        </div>
                    )}

                    <p style={{ marginTop: 20, fontSize: 13, color: "#999" }}>
                        Need help? Contact your clinic representative.
                    </p>
                </div>
            )}

            {status === "error" && (
                <>
                    <div style={{ fontSize: 60, marginBottom: 20 }}>⚠️</div>
                    <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12, color: "#1a1a1a" }}>
                        Link Expired or Invalid
                    </h2>
                    <p style={{ fontSize: 15, color: "#666", maxWidth: 320 }}>
                        We couldn't find this receipt. Please contact your doctor for a new link.
                    </p>
                </>
            )}

            <style>{`
                .spinner {
                    width: 48px;
                    height: 48px;
                    border: 5px solid #e0d6d6;
                    border-top-color: #a51717;
                    border-radius: 50%;
                    animation: spin 1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
                    margin-bottom: 24px;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default ReceiptDownload;
